<template>
    <div class="footer-section">
        <div class="terms" @click="checked = !checked">
            <PrimeCheckbox class="mt-1" v-model="checked" binary @click.stop />
            <p>
                I agree to the
                <a href="https://privacybee.com/terms-of-service/" target="_blank" @click.stop> Terms of Service </a>
                &
                <a href="https://privacybee.com/privacy-policy/" target="_blank" @click.stop> Privacy Policy </a>
            </p>
        </div>
        <div class="terms" @click="renewal = !renewal">
            <PrimeCheckbox v-model="renewal" binary @click.stop />
            <p>I agree to take financial responsibility for any gifted licenses and renewals.</p>
        </div>
        
        <!-- Existing Payment Method Section -->
        <div v-if="hasExistingPaymentMethod" class="payment-method-section">
            <div class="existing-payment" @click="useExistingPayment = true">
                <PrimeRadioButton 
                    v-model="useExistingPayment" 
                    :value="true" 
                    class="h-5 w-5"
                />
                <div class="payment-info">
                    <span class="payment-text">Pay with {{ authStore.user?.identity?.payment_method?.type?.toUpperCase() }} ending {{ authStore.user?.identity?.payment_method?.last_four }}</span>
                </div>
            </div>
            <div class="new-payment"  @click="useExistingPayment = false">
                <PrimeRadioButton 
                    v-model="useExistingPayment" 
                    :value="false" 
                    class="h-5 w-5"
                />
                <div class="payment-info">
                    <span class="payment-text">Enter new card details</span>
                </div>
            </div>
        </div>
        
        <div class="w-full">
            <PrimeButton
                label="Purchase"
                :disabled="!checked || !renewal"
                @click="handlePurchase"
                class="w-full"
                :loading="loader"
                />
        </div>
        <span> Subscription will automatically renew until canceled. </span>
        <span class="usd-disclaimer"> All prices are in USD </span>
    </div>
</template>
<script setup lang="ts">
import PrimeButton from 'primevue/button';
import PrimeCheckbox from 'primevue/checkbox';
import PrimeRadioButton from 'primevue/radiobutton';
import { useDynamicDialog } from '~/composables/dynamic-dialog';
import { licenseService } from '~/services/license-service';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { usePersonStore } from '~/stores/person';
import { useBillingStore } from '~/stores/person/billing';
import { useContactsStore } from '~/stores/person/contacts';
import { LicenseRenewalTermKey, PurchaseType } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';

const checked = ref(false);
const renewal = ref(false);
const { showSuccess, showError, showWarning } = useGlobalToast();
const authStore = useAuthStore();
const store = useSubscribeStore();
const nuxtApp = useNuxtApp();
const loader = ref(false);
const dialogRef = getDialogRef(ModalNames.PURCHASE_LICENSES);
const personStore = usePersonStore();
const { selectedTerm } = storeToRefs(useSubscribeStore());
const paymentMethodId = ref('');
const useExistingPayment = ref(true);
const billingStore = useBillingStore();

const hasExistingPaymentMethod = computed(() => {
    return authStore.user?.identity?.payment_method?.type;
});

const {
    license,
    selectedPlanType,
    purchaseType
} = storeToRefs(store);

const closeDialog = () => {
    dialogRef.value.close();
};

const handlePurchase = () => {
    if (useExistingPayment.value && hasExistingPaymentMethod.value) {
        // Use existing payment method - call handleSubscription directly
        handleSubscription();
    } else {
        // Open payment modal for new card details
        handleShowPaymentModal();
    }
};

const handleShowPaymentModal = () => {
    // Reset payment method ID before opening modal
    paymentMethodId.value = '';
    
    // Listen for payment success using Nuxt hook system
    const nuxtApp = useNuxtApp();
    const hook = nuxtApp.hook('payment_method:success', (payload: { paymentMethodId: string }) => {
        paymentMethodId.value = payload.paymentMethodId;
        handleSubscription();
        // Remove the hook after it's used
        hook();
    });
    
    useDynamicDialog(ModalNames.STRIPE_PAYMENT);
}

const handleSubscription = async () => {
    loader.value = true;

    if (purchaseType.value === PurchaseType.UPGRADE) {
        const upgradeData = {
            new_license: store.selectedPlanType,
            term: selectedTerm.value
        };

        try {
            await licenseService.changeLicense(authStore.user?.identity?.id!, license.value?.id!, upgradeData);
            await personStore.getPerson();
            await authStore.getMe();
            
            closeDialog();
            showSuccess('Success', 'License modified successfully!');
        } catch (error: any) {
            showError('Error', error.message || 'Failed to upgrade license');
        } finally {
            loader.value = false;
        }
        return;
    }

    // Handle regular purchase case
    const data: {
        subscription_type: string;
        licenses: {
            license: string;
            count: number;
        };
        payment_method?: string;
    } = {
        subscription_type: LicenseRenewalTermKey[selectedTerm.value],
        licenses: {
            license: store.selectedPlanType.toLowerCase(),
            count: store.licenseCount,
        },
    };

    // Only include payment_method if we're using a new card
    if (paymentMethodId.value) {
        data.payment_method = paymentMethodId.value;
    }

    if ( !hasExistingPaymentMethod.value && !paymentMethodId.value) {
        showError('Error', 'There was an issue creating your payment method. Please try again.');
        return;
    }

    try {
        const result = await licenseService.purchaseLicenses(authStore.user?.identity?.id!, data);

        // Check if 3D Secure authentication is required
        if (result && result.data && result.data.requires_action && result.data.payment_intent_client_secret) {
            // Handle 3D Secure authentication
            await handle3DSecureAuthentication(result.data);
            return;
        }

        await personStore.getPerson();
        await authStore.getMe();
        await billingStore.fetchInvoices();

        if ( dialogRef.value?.data?.type === PurchaseType.GIFT) {
            const license = personStore.person?.licenses.find(license => {
                return license.slug === store.selectedPlanType && license.term === selectedTerm.value && license.expired === false && !license.assigned_to
            });
            if ( !license ) {
                showError('Error', 'There was an issue assigning the license. Please try again.');
                // redirect to billing page
                closeDialog();

                return
            }

            await store.assignLicense(license.id, dialogRef.value?.data?.contact!);

            const contactsStore = useContactsStore();
            await contactsStore.fetchContacts();
        }

        
        closeDialog();
        showSuccess('Success', 'Congratulations! License(s) purchased successfully.');
    } catch (error: any) {
        showError('Error', error.message || 'Failed to purchase licenses');
    } finally {
        loader.value = false;
    }
};

const handle3DSecureAuthentication = async (result: any) => {
    try {
        const stripeModule = await import('@stripe/stripe-js');
        const stripe = await stripeModule.loadStripe(useRuntimeConfig().public.pb.stripeKey);
        
        const { error, paymentIntent } = await stripe!.confirmCardPayment(result.payment_intent_client_secret);
        
        if (error) {
            showError('Error', error.message || '3D Secure authentication failed');
            loader.value = false;
            return;
        }

        if (paymentIntent.status === 'succeeded') {
            // Confirm the payment with our backend
            await licenseService.confirmPayment(authStore.user?.identity?.id!, {
                payment_intent_id: paymentIntent.id,
                invoice_id: result.invoice_id
            });

            await personStore.getPerson();
            await authStore.getMe();
            useBillingStore().fetchInvoices();

            if (dialogRef.value?.data?.type === PurchaseType.GIFT) {
                const license = personStore.person?.licenses.find(license => {
                    return license.slug === store.selectedPlanType && license.term === selectedTerm.value && license.expired === false && !license.assigned_to
                });
                if (!license) {
                    showError('Error', 'There was an issue assigning the license. Please try again.');
                    closeDialog();
                    return;
                }

                await store.assignLicense(license.id, dialogRef.value?.data?.contact!);

                const contactsStore = useContactsStore();
                await contactsStore.fetchContacts();
            }

            closeDialog();
            showSuccess('Success', 'Congratulations! License(s) purchased successfully.');
        } else {
            showError('Error', 'Payment was not successful');
        }
    } catch (error: any) {
        showError('Error', error.message || 'Failed to complete 3D Secure authentication');
    } finally {
        loader.value = false;
    }
};
</script>
<style lang="scss" scoped>
.footer-section {
    @apply flex flex-col items-start gap-2.5 pt-5 self-stretch;

    span {
        @apply self-stretch text-zinc-500 text-center font-inter text-xs font-normal leading-6;
    }

    .terms {
        @apply flex pb-2 items-start gap-2 cursor-pointer select-none;

        p {
            @apply text-zinc-500 font-inter text-sm font-normal leading-5;
        }

        a {
            @apply text-blue-600 underline;
        }
    }

    .payment-method-section {
        @apply flex flex-col items-start gap-2 w-full;

        .existing-payment, .new-payment {
            @apply flex items-center gap-2 w-full cursor-pointer;
        }

        .payment-info {
            @apply flex-1;

            .payment-text {
                @apply text-zinc-500 font-inter text-sm font-normal leading-5;
            }
        }
    }
}

.usd-disclaimer {
    @apply text-xs text-zinc-500 font-inter mt-2;
}
</style>
